/**
 * JSON-backed persistence for quest records. Mirrors the persistQueue
 * pattern from `loop-service.ts` so concurrent writes serialize cleanly.
 *
 * Storage layout: $APPOSTLE_HOME/quests/quests.json
 * Survives daemon restart. On boot, callers (QuestService.initialize)
 * are responsible for converting any `running` records to `stopped` since
 * in-flight runners are lost when the daemon goes down.
 */

import { promises as fs } from "node:fs";
import path from "node:path";
import { z } from "zod";
import type { Logger } from "pino";
import { QuestRecordSchema, type QuestRecord } from "./types.js";

const StoredQuestsSchema = z.array(QuestRecordSchema);

export interface QuestStoreOptions {
  appostleHome: string;
  logger: Logger;
}

export class QuestStore {
  private readonly filePath: string;
  private readonly logger: Logger;
  private records: Map<string, QuestRecord>;
  private persistQueue: Promise<void>;

  constructor(options: QuestStoreOptions) {
    this.filePath = path.join(options.appostleHome, "quests", "quests.json");
    this.logger = options.logger;
    this.records = new Map();
    this.persistQueue = Promise.resolve();
  }

  async initialize(): Promise<void> {
    try {
      const raw = await fs.readFile(this.filePath, "utf8");
      const parsed = StoredQuestsSchema.parse(JSON.parse(raw));
      this.records = new Map(parsed.map((r) => [r.id, r]));
    } catch (err: unknown) {
      const code =
        err && typeof err === "object" && "code" in err ? (err as { code: string }).code : null;
      if (code === "ENOENT") {
        this.records = new Map();
        return;
      }
      this.logger.warn(
        { err: err instanceof Error ? err.message : String(err) },
        "quest-store: failed to read quests.json — starting empty",
      );
      this.records = new Map();
    }
  }

  list(): QuestRecord[] {
    return [...this.records.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  get(id: string): QuestRecord | null {
    return this.records.get(id) ?? null;
  }

  async upsert(record: QuestRecord): Promise<void> {
    this.records.set(record.id, record);
    await this.persist();
  }

  async delete(id: string): Promise<void> {
    if (!this.records.delete(id)) {
      return;
    }
    await this.persist();
  }

  private async persist(): Promise<void> {
    this.persistQueue = this.persistQueue
      .then(async () => {
        await fs.mkdir(path.dirname(this.filePath), { recursive: true });
        const payload = JSON.stringify([...this.records.values()], null, 2);
        await fs.writeFile(this.filePath, payload, "utf8");
      })
      .catch((err: unknown) => {
        this.logger.error(
          { err: err instanceof Error ? err.message : String(err) },
          "quest-store: persist failed",
        );
      });
    return this.persistQueue;
  }
}
